import { useState, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter, Row } from "@/components/Table";
import { ColumnSort } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import { useSearchWithParams, useParams as useParamsHook } from "@/hooks/useSearchParams";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePagination from "@/hooks/usePagination";
import MultiSelect from "@/components/MultiSelect";
import SearchInput from "@/components/SearchInput";
import { STUDENTS_QUERY } from "../students/queries";
import { UniversityStudentsQuery, PatStatus, UniversityStudentSimplifiedNode } from "../students/types";
import { buildPATStatusForNonMembers } from "@/features/person/utils/utils";
import { PAT_STATUS_OPTIONS, mapPATStatusesToGraphQL } from "../../_shared/constants";
import { getStatusTagColor } from "../../[universityId]/_utils";
import { toDateFormat } from "@/lib/dateHelpers";
import Tag from "@/components/Tag";
import Icon from "@/components/Icon";
import { buildAvatarDetailsColumn } from "@/components/Table";
import PATActivityTable from "@/features/person/components/PATActivityTable";
import { buildUniversityStudentDetailLink } from "../../_shared/_utils";

type PlayingAbilityTestStatus = NonNullable<
  UniversityStudentSimplifiedNode["prospectProgram"]
>["playingAbilityTestStatus"];

interface PATStatusRow {
  id: string;
  pgaId: string;
  displayName: string;
  firstName: string;
  lastName: string;
  profilePhoto: string;
  patStatus: string;
  activeSuspension: boolean;
  patDate: string | null;
  patScore: number | null;
  playingAbilityTestStatus: PlayingAbilityTestStatus | null;
}

export default function PATStatusesPage() {
  const location = useLocation();
  const { universityId } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination({
    defaultPageSize: 20
  });
  const { isCompactLayout } = useIsCompactLayout();

  const { params, addSearchParam, deleteSearchParams } = useParamsHook();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });

  const initialPatStatus = useMemo(
    () =>
      params.status && params.status.length > 0
        ? mapPATStatusesToGraphQL((params.status as string).split(",").filter(Boolean))
        : undefined,
    [params.status]
  );

  const [selectedPatStatus, setSelectedPatStatus] = useState<PatStatus[]>(
    params.status ? ((params.status as string).split(",").filter(Boolean) as PatStatus[]) : []
  );
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([{ id: "PAT_STATUS", desc: true }]);

  const getOrderByVariable = useCallback(() => {
    if (orderBy.length === 0) return undefined;
    const { id, desc } = orderBy[0];
    return {
      field: id,
      direction: desc ? "DESC" : "ASC"
    };
  }, [orderBy]);

  const { data, loading } = useQuery<UniversityStudentsQuery>(STUDENTS_QUERY, {
    variables: {
      ...paginationVariables,
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: debouncedSearch || undefined,
        patStatus: initialPatStatus as PatStatus[] | undefined,
        orderBy: getOrderByVariable()
      }
    },
    skip: !universityId
  });

  const handleSort = useCallback(
    (updaterOrValue: any) => {
      const sortState = typeof updaterOrValue === "function" ? updaterOrValue(orderBy) : updaterOrValue;
      setOrderBy(sortState);
      resetPagination();
    },
    [orderBy, resetPagination]
  );

  const patStatusRows: PATStatusRow[] = useMemo(() => {
    if (!data?.universityStudents?.nodes) return [];

    const rows = data.universityStudents.nodes.map((node) => {
      const patStatus = node.prospectProgram?.playingAbilityTestStatus;
      const activeSuspension = !!patStatus?.activeSuspension;

      if (!node.prospectProgram || !patStatus) {
        return {
          id: node.id,
          pgaId: node.pgaId,
          displayName: node.displayName,
          firstName: node.firstName,
          lastName: node.lastName,
          profilePhoto: node.profilePhoto,
          patStatus: "Not Started",
          activeSuspension: false,
          patDate: null,
          patScore: null,
          playingAbilityTestStatus: null
        };
      }

      const statusResult = buildPATStatusForNonMembers(patStatus as any);
      let rawStatus = (patStatus.status || "").toUpperCase();
      if (rawStatus === "QUALIFIED") {
        rawStatus = "QUALIFYING";
      }
      const patStatusValue = activeSuspension ? "SUSPENDED" : rawStatus || statusResult.value.toUpperCase();

      const submissions = patStatus.submissions || [];
      const sortedSubmissions = [...submissions]
        .filter((submission) => submission.date)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      let patDate: string | null = null;
      let patScore: number | null = null;

      if (patStatusValue === "PASSED") {
        const passedSubmissions = sortedSubmissions.filter((sub) => sub.result?.toUpperCase() === "PASSED");
        const mostRecentPassedSubmission = passedSubmissions[0] || null;

        if (mostRecentPassedSubmission) {
          patDate = mostRecentPassedSubmission.date || null;
          if (mostRecentPassedSubmission.totalScore !== null && mostRecentPassedSubmission.totalScore !== undefined) {
            patScore = mostRecentPassedSubmission.totalScore;
          } else if (
            mostRecentPassedSubmission.roundOneScore !== null &&
            mostRecentPassedSubmission.roundTwoScore !== null
          ) {
            patScore =
              (mostRecentPassedSubmission.roundOneScore || 0) + (mostRecentPassedSubmission.roundTwoScore || 0);
          } else if (mostRecentPassedSubmission.roundOneScore !== null) {
            patScore = mostRecentPassedSubmission.roundOneScore ?? null;
          }
        }
      } else if (patStatusValue === "FAILED") {
        const failedSubmissions = sortedSubmissions.filter((sub) => sub.result?.toUpperCase() === "FAILED");
        const mostRecentFailedSubmission = failedSubmissions[0] || null;

        if (mostRecentFailedSubmission) {
          patDate = mostRecentFailedSubmission.date || null;
          const totalScore = mostRecentFailedSubmission.totalScore;
          if (totalScore !== null && totalScore !== undefined && totalScore > 0) {
            patScore = totalScore;
          } else if (
            mostRecentFailedSubmission.roundOneScore !== null &&
            mostRecentFailedSubmission.roundTwoScore !== null
          ) {
            patScore =
              (mostRecentFailedSubmission.roundOneScore || 0) + (mostRecentFailedSubmission.roundTwoScore || 0);
          } else if (mostRecentFailedSubmission.roundOneScore !== null) {
            patScore = mostRecentFailedSubmission.roundOneScore ?? null;
          } else if (mostRecentFailedSubmission.roundTwoScore !== null) {
            patScore = mostRecentFailedSubmission.roundTwoScore ?? null;
          }
        }
      } else if (patStatusValue === "QUALIFYING") {
        const qualifyingSubmissions = sortedSubmissions.filter(
          (sub) => sub.result?.toUpperCase() === "QUALIFIED" || sub.result?.toUpperCase() === "QUALIFYING"
        );
        const mostRecentQualifyingSubmission = qualifyingSubmissions[0] || null;

        if (mostRecentQualifyingSubmission) {
          patDate = mostRecentQualifyingSubmission.date || null;
          patScore = null;
        }
      }

      const displayStatus = patStatusValue
        .split("_")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

      return {
        id: node.id,
        pgaId: node.pgaId,
        displayName: node.displayName,
        firstName: node.firstName,
        lastName: node.lastName,
        profilePhoto: node.profilePhoto,
        patStatus: displayStatus,
        activeSuspension,
        patDate,
        patScore,
        playingAbilityTestStatus: patStatus
      };
    });

    return rows;
  }, [data]);

  const columns = useMemo(() => {
    const baseColumns = [
      ...buildAvatarDetailsColumn<PATStatusRow>({
        isCompactLayout,
        getTitle: (row) => row.displayName,
        getSubtitle: (row) => `PGA ID: ${row.pgaId}`,
        getPicture: (row) => row.profilePhoto,
        size: 280,
        minSize: 220
      }),
      {
        id: "PAT_STATUS",
        accessorKey: "patStatus",
        header: "Status",
        cell: ({ row }: { row: Row<PATStatusRow> }) => {
          const status = row.original.patStatus;
          return (
            <Tag tagType={getStatusTagColor(status)} size="small">
              {status}
            </Tag>
          );
        },
        enableSorting: true,
        meta: { sortType: "text" as const },
        size: 120
      },
      {
        accessorKey: "activeSuspension",
        header: "Suspension",
        cell: ({ row }: { row: Row<PATStatusRow> }) => {
          if (row.original.activeSuspension) {
            return <span className="text-sys-brand-error font-medium">Y</span>;
          }
          return <span className="opacity-20">--</span>;
        },
        enableSorting: false,
        size: 100
      },
      {
        accessorKey: "patDate",
        header: "PAT Date",
        cell: ({ row }: { row: Row<PATStatusRow> }) => {
          const date = row.original.patDate;
          if (!date) {
            return <span className="opacity-20">--</span>;
          }
          return toDateFormat(date);
        },
        enableSorting: false,
        size: 120
      },
      {
        accessorKey: "patScore",
        header: "PAT Score",
        cell: ({ row }: { row: Row<PATStatusRow> }) => {
          const score = row.original.patScore;
          const isExpanded = row.getIsExpanded();

          return (
            <button
              title="View PAT Results"
              className="flex items-center gap-1 cursor-pointer w-full"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
            >
              <span>{score === null || score === undefined ? <span className="opacity-20">--</span> : score}</span>
              <Icon name={isExpanded ? "expand_less" : "expand_more"} />
            </button>
          );
        },
        enableSorting: false,
        size: 100
      }
    ];

    return baseColumns;
  }, [isCompactLayout]);

  const filterState = {
    status: selectedPatStatus.length > 0
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = useCallback(() => {
    setSelectedPatStatus([]);
    deleteSearchParams(["status"]);
    clearSearch();
    resetPagination();
  }, [deleteSearchParams, clearSearch, resetPagination]);

  const hasActiveFilters = searchValue || activeFilterCount > 0;

  return (
    <>
      <TableHeading
        heading="PAT Statuses"
        body="View all current student PAT (Playing Ability Test) statuses for this PGM University Program"
      />
      <Table
        data={patStatusRows}
        loading={loading}
        columns={columns}
        sortingState={orderBy}
        onSortingChange={handleSort}
        serverSideSorting={true}
        getRowLink={(row) => {
          const pgaId = row.original.pgaId;
          if (!universityId || !pgaId) {
            return null;
          }

          return buildUniversityStudentDetailLink({
            universityId,
            pgaId,
            returnPath: `${location.pathname}${location.search}`,
            subPath: "pat-results"
          });
        }}
        data-testid="pat-statuses-table"
        renderExpandedRow={({ row }) => (
          <PATActivityTable
            patStatus={row.original.playingAbilityTestStatus as any}
            asDetailsPanel={true}
            enableNavigation={true}
            pgaId={row.original.pgaId}
            refetchQueries={[STUDENTS_QUERY]}
          />
        )}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No PAT Status Data"
            caption="There are no PAT status records to display at this time"
            iconName="sports_golf"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Matching PAT Statuses"
            caption="Try adjusting your search or filters."
            iconName="search"
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(data?.universityStudents)}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
          >
            <SearchInput
              placeholder="Search Students"
              query={searchValue}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
            />
            <div data-name="status">
              <MultiSelect
                label="PAT Status"
                options={PAT_STATUS_OPTIONS}
                selectedValues={selectedPatStatus}
                onSelect={(value, checked) => {
                  const updatedStatuses = checked
                    ? [...selectedPatStatus, value as PatStatus]
                    : selectedPatStatus.filter((v) => v !== value);

                  setSelectedPatStatus(updatedStatuses);

                  if (updatedStatuses.length > 0) {
                    addSearchParam("status", updatedStatuses.join(","));
                  } else {
                    deleteSearchParams("status");
                  }
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedPatStatus([]);
                  deleteSearchParams("status");
                  resetPagination();
                }}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.universityStudents)} />}
      />
    </>
  );
}
